import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CopyOutlined } from '@ant-design/icons';
import { Customer, Task } from '@bringg/types';
import { Button, Divider, FormInstance, notification, RadioChangeEvent, Tooltip, Typography } from 'antd';

import {
  AvailableDeliveryTime,
  BackgroundTasks,
  InventoriesList,
  LoginDriverQRCode,
  ShippingDetails,
  ShippingOptions,
} from 'Components';
import { ShippingOption } from 'Consts';
import { useInventories } from 'Hooks';
import { useAuth, useDemoList } from 'Providers';
import { airbrake, createCurbsideDeliveryTask } from 'Services';
import { DeliveryOptions, DemoFlows, ECommerceSettings, ShippingOptions as ShippingOptionsType } from 'Types';
import { getWebUrl, mapDemoToSettings, sleep } from '../../helpers';

import './styles.scss';

export function DemoExecution() {
  const { demoIndex } = useParams<{ demoIndex: string }>();
  const { demoList, backgroundTasksList } = useDemoList();
  const demo = demoList.data[Number(demoIndex)];
  const backgroundTasksDemo = backgroundTasksList[Number(demoIndex)];
  const settings = mapDemoToSettings<ECommerceSettings>(demo) as ECommerceSettings;
  const backgroundTasksSettings = mapDemoToSettings<ECommerceSettings>(backgroundTasksDemo);

  const [inventories, setInventories] = useInventories();
  const [isLoading, setIsLoading] = useState(false);
  const [findButtonPressed, setFindButtonPressed] = useState(false);
  const [backgroundStarted, setBackgroundStarted] = useState(false);

  const { dashboardSdk } = useAuth();

  const [shippingDetails, setShippingDetails] = useState<ShippingOptionsType>({
    phone: demo
      ? demo.fields.find((field) => field.name === 'phone')?.value
      : {
          phone: '',
          code: null,
          short: '',
        },
  } as ShippingOptionsType);
  const [shippingForm, setShippingForm] = useState<FormInstance | null>(null);
  const [shippingOption, setShippingOption] = useState<ShippingOption>(ShippingOption.Home);
  const [deliveryOption, setDeliveryOption] = useState<DeliveryOptions | null>(null);
  const [task, setTask] = useState<Task | null>(null);

  const customer = useMemo<Customer>(
    () =>
      ({
        allow_sending_sms: true,
        address: shippingOption === ShippingOption.Self ? undefined : shippingDetails.address,
        name: `${shippingDetails.firstName} ${shippingDetails.lastName}`,
        phone: `+${shippingDetails.phone.code}${shippingDetails.phone.phone}`,
      } as Customer),
    [shippingDetails, shippingOption]
  );

  const updateShipping = (update: Partial<ShippingOptionsType>) =>
    setShippingDetails((prev) => ({
      ...prev,
      ...update,
    }));

  const createTask = shippingOption === ShippingOption.Home ? demo.createTask : createCurbsideDeliveryTask;

  const onSubmit = async () => {
    setIsLoading(true);
    try {
      await shippingForm?.validateFields();
    } catch (ex) {
      setIsLoading(false);
      airbrake?.notify(ex);
      return;
    }

    let task: Task | null = null;
    try {
      task = (await createTask(
        settings,
        customer,
        inventories,
        deliveryOption as DeliveryOptions,
        dashboardSdk
      )) as Task;
    } catch (ex) {
      if (ex instanceof Error) {
        airbrake?.notify(ex);
        notification.error({
          placement: 'bottomRight',
          message: ex.message,
        });
      }
    }

    setIsLoading(false);
    setTask(task);

    const generateBackgroundTasks = settings?.shouldGenerateBackgroundTasks && shippingOption === ShippingOption.Home;

    if (generateBackgroundTasks) {
      await sleep(10_000);
      setBackgroundStarted(true);
    }
  };

  if (!settings) {
    return null;
  }

  const hideDeliveries = shippingOption !== ShippingOption.Home || !findButtonPressed;

  return (
    <div className="home">
      <Typography.Title level={1}>Checkout</Typography.Title>
      <InventoriesList inventories={inventories} onChange={setInventories} />
      <Divider />
      <Typography.Title level={2}>{`${
        shippingOption === ShippingOption.Home ? 'Shipping' : 'Customer'
      } details`}</Typography.Title>
      <ShippingDetails
        settings={settings}
        shippingOption={shippingOption}
        onChange={updateShipping}
        onFormInit={setShippingForm}
      />
      <Divider />
      <Typography.Title level={2}>Shipping options</Typography.Title>
      <ShippingOptions
        option={shippingOption}
        onFindClick={() => setFindButtonPressed(true)}
        onChange={(e: RadioChangeEvent) => {
          setShippingOption(e.target.value);
          setFindButtonPressed(false);
        }}
      />
      <div className={`available-delivery ${hideDeliveries ? 'collapsed' : ''}`}>
        <Divider />
        <Typography.Title level={2}>Available deliveries</Typography.Title>
        {!hideDeliveries && (
          <AvailableDeliveryTime
            option={deliveryOption as DeliveryOptions}
            demoType={demo.demoType}
            onChange={setDeliveryOption}
          />
        )}
      </div>
      <Divider />
      <div className="home-footer">
        {!task && (
          <Button disabled={isLoading} type="primary" size="large" loading={isLoading} onClick={onSubmit}>
            Place Order
          </Button>
        )}
        {task && (
          <div className="completed">
            <Typography.Title level={2}>
              Your order{' '}
              <a href={`${getWebUrl(dashboardSdk)}/#/list`} target="_blank" rel="noreferrer">
                {task.external_id}
              </a>{' '}
              <Tooltip title="Copy">
                <Button
                  className="copy-button"
                  type="primary"
                  shape="circle"
                  icon={<CopyOutlined />}
                  onClick={() => navigator.clipboard.writeText(task.external_id as string)}
                />
              </Tooltip>
              has been created
              {demo.demoType === DemoFlows.InternalDriver && <LoginDriverQRCode userId={task.user_id as number} />}
            </Typography.Title>
          </div>
        )}
      </div>
      {backgroundStarted && (
        <BackgroundTasks
          settings={backgroundTasksSettings as ECommerceSettings}
          inventories={inventories}
          customer={customer}
          createTask={createTask}
        />
      )}
    </div>
  );
}
